// ===== FIXED AUTHENTICATION CONTEXT =====
// src/contexts/AuthContext.js
import React, { createContext, useContext, useReducer, useEffect } from "react";
import { ROLES } from "../constants/roles";
import { PERMISSIONS } from "../constants/permissions";

const AuthContext = createContext();

// Role-Permission mapping
const ROLE_PERMISSIONS = {
  [ROLES.SUPER_ADMIN.key]: ROLES.SUPER_ADMIN.permissions,
  [ROLES.ADMIN.key]: ROLES.ADMIN.permissions,
  [ROLES.MANAGER.key]: ROLES.MANAGER.permissions,
  [ROLES.TEAM_LEAD.key]: ROLES.TEAM_LEAD.permissions,
  [ROLES.USER.key]: ROLES.USER.permissions,
  [ROLES.VIEWER.key]: ROLES.VIEWER.permissions,
  [ROLES.GUEST.key]: ROLES.GUEST.permissions,
};

const initialState = {
  user: null,
  token: localStorage.getItem("token"),
  isAuthenticated: false,
  loading: true,
  permissions: [],
  lastActivity: Date.now(),
};

function authReducer(state, action) {
  switch (action.type) {
    case "LOGIN_START":
      return { ...state, loading: true };

    case "LOGIN_SUCCESS":
      const userPermissions = [
        ...(ROLE_PERMISSIONS[action.payload.user.role] || []),
        ...(action.payload.user.customPermissions || []),
      ];

      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        isAuthenticated: true,
        loading: false,
        permissions: userPermissions,
        lastActivity: Date.now(),
      };

    case "LOGIN_FAILURE":
      return {
        ...state,
        loading: false,
        isAuthenticated: false,
        user: null,
        token: null,
        permissions: [],
      };

    case "LOGOUT":
      localStorage.removeItem("token");
      localStorage.removeItem("refreshToken");
      return {
        ...initialState,
        loading: false,
        token: null,
      };

    case "LOAD_USER":
      const loadUserPermissions = [
        ...(ROLE_PERMISSIONS[action.payload.role] || []),
        ...(action.payload.customPermissions || []),
      ];

      return {
        ...state,
        user: action.payload,
        isAuthenticated: true,
        loading: false,
        permissions: loadUserPermissions,
        lastActivity: Date.now(),
      };

    case "UPDATE_ACTIVITY":
      return {
        ...state,
        lastActivity: Date.now(),
      };

    case "UPDATE_USER":
      return {
        ...state,
        user: { ...state.user, ...action.payload },
        lastActivity: Date.now(),
      };

    default:
      return state;
  }
}

export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Load user on app start
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      loadUser(token);
    } else {
      dispatch({ type: "LOGIN_FAILURE" });
    }
  }, []);

  // Session timeout check
  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      const sessionTimeout = 60 * 60 * 1000; // 1 hour

      if (state.isAuthenticated && now - state.lastActivity > sessionTimeout) {
        logout();
        alert("Session expired. Please login again.");
      }
    }, 60000); // Check every minute

    return () => clearInterval(interval);
  }, [state.isAuthenticated, state.lastActivity]);

  const loadUser = async (token) => {
    try {
      const response = await fetch("/api/auth/me", {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (response.ok) {
        const user = await response.json();
        dispatch({ type: "LOAD_USER", payload: user });
      } else {
        localStorage.removeItem("token");
        localStorage.removeItem("refreshToken");
        dispatch({ type: "LOGIN_FAILURE" });
      }
    } catch (error) {
      console.error("Error loading user:", error);
      localStorage.removeItem("token");
      localStorage.removeItem("refreshToken");
      dispatch({ type: "LOGIN_FAILURE" });
    }
  };

  const login = async (credentials) => {
    dispatch({ type: "LOGIN_START" });

    try {
      const formData = new FormData();
      formData.append("phone", credentials.phone);
      formData.append("password", credentials.password);

      const response = await fetch("/api/auth/login", {
        method: "POST",
        body: formData,
      });

      const data = await response.json();

      if (response.ok) {
        localStorage.setItem("token", data.token);
        if (data.refreshToken) {
          localStorage.setItem("refreshToken", data.refreshToken);
        }

        dispatch({
          type: "LOGIN_SUCCESS",
          payload: { user: data.user, token: data.token },
        });

        return { success: true, data };
      } else {
        dispatch({ type: "LOGIN_FAILURE" });
        return { success: false, error: data.message || "Login failed" };
      }
    } catch (error) {
      dispatch({ type: "LOGIN_FAILURE" });
      return { success: false, error: "Network error. Please try again." };
    }
  };

  const logout = async () => {
    try {
      const token = localStorage.getItem("token");
      if (token) {
        await fetch("/api/auth/logout", {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });
      }
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      dispatch({ type: "LOGOUT" });
    }
  };

  const updateActivity = () => {
    if (state.isAuthenticated) {
      dispatch({ type: "UPDATE_ACTIVITY" });
    }
  };

  const hasPermission = (permission) => {
    return state.permissions.includes(permission);
  };

  const hasRole = (role) => {
    return state.user?.role === role;
  };

  const hasAnyRole = (roles) => {
    return roles.includes(state.user?.role);
  };

  const hasAllPermissions = (permissions) => {
    return permissions.every((permission) =>
      state.permissions.includes(permission)
    );
  };

  const hasAnyPermission = (permissions) => {
    return permissions.some((permission) =>
      state.permissions.includes(permission)
    );
  };

  const refreshToken = async () => {
    try {
      const refreshToken = localStorage.getItem("refreshToken");
      if (!refreshToken) {
        throw new Error("No refresh token available");
      }

      const response = await fetch("/api/auth/refresh", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ refreshToken }),
      });

      const data = await response.json();

      if (response.ok) {
        localStorage.setItem("token", data.token);
        if (data.refreshToken) {
          localStorage.setItem("refreshToken", data.refreshToken);
        }

        // Update the token in state
        dispatch({
          type: "LOGIN_SUCCESS",
          payload: { user: state.user, token: data.token },
        });

        return { success: true, token: data.token };
      } else {
        throw new Error(data.message || "Token refresh failed");
      }
    } catch (error) {
      console.error("Token refresh error:", error);
      logout();
      return { success: false, error: error.message };
    }
  };

  const updateProfile = async (profileData) => {
    try {
      const token = localStorage.getItem("token");
      const formData = new FormData();

      Object.entries(profileData).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
          formData.append(key, value);
        }
      });

      const response = await fetch(`/api/users/${state.user.id}`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      const data = await response.json();

      if (response.ok) {
        dispatch({ type: "UPDATE_USER", payload: data });
        return { success: true, data };
      } else {
        return {
          success: false,
          error: data.message || "Profile update failed",
        };
      }
    } catch (error) {
      console.error("Profile update error:", error);
      return { success: false, error: "Network error. Please try again." };
    }
  };

  const value = {
    ...state,
    login,
    logout,
    refreshToken,
    updateProfile,
    updateActivity,
    hasPermission,
    hasRole,
    hasAnyRole,
    hasAllPermissions,
    hasAnyPermission,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

// Export role and permission constants for easy access
export { ROLES, PERMISSIONS };

export default AuthContext;
